import { useEffect, useState } from 'react';
import { CustomerPickerDrawer } from './CustomerPickerDrawer';
import { useAuth } from '../store/auth';
import { Badge, Modal } from './ui';
import { api, ApiError } from '../lib/api';
import { amount, percentFromBps } from '../lib/format';
import { normalizeOpenItemName, openItemNameProblem, type BillDiscount } from '@sp/core';
import type { Customer, HeldSale } from '../lib/types';
import { tr, useT } from '../i18n';

/** Shared chrome: every till dialog traps focus, closes on Esc and returns focus to the scanner. */
function Dialog({
  title,
  subtitle,
  onClose,
  children,
  footer,
  testId,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  testId?: string;
}) {
  return (
    <Modal title={title} subtitle={subtitle} onClose={onClose} footer={footer} restoreFocus="barcode" testId={testId}>
      {children}
    </Modal>
  );
}

/**
 * Customer lookup by mobile number.
 *
 * Phone is the key in an Indian supermarket, so this is one field and a button: type the
 * ten digits, press Enter. A new customer can be created from the same screen rather than
 * making the cashier leave the bill.
 */
export function CustomerDialog({ onClose, onAttached, initialPhone, flowId = 'legacy-customer' }: { onClose: () => void; onAttached: (customer: Customer) => void; initialPhone?: string; flowId?: string }) {
  const canCreate = useAuth(s => s.can('pos.customer.create'));
  return <CustomerPickerDrawer flowId={flowId} initialPhone={initialPhone} canCreate={canCreate} onSelect={onAttached} onClose={onClose} />;
}

/**
 * Bill discount.
 *
 * The ceiling comes from the server and reflects what this role is allowed to give away.
 * Showing it rather than hiding it is deliberate: a cashier who cannot see the limit will
 * find out by being refused in front of a customer.
 */
export function BillDiscountDialog({
  subtotalPaise,
  maxBps,
  value,
  onApply,
  onClose,
}: {
  subtotalPaise: number;
  maxBps: number;
  value: BillDiscount;
  onApply: (discount: BillDiscount) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [percent, setPercent] = useState(value.type === 'percent' ? String(value.bps / 100) : '');
  const [rupees, setRupees] = useState('');
  const [kind, setKind] = useState<'percent' | 'amount' | 'none'>(value.type);

  // A flat amount is judged by the percentage of the bill it represents, as the server does.
  const requestedBps =
    kind === 'percent'
      ? Math.round(Number.parseFloat(percent || '0') * 100)
      : kind === 'amount' && subtotalPaise > 0
        ? Math.ceil((Math.round(Number.parseFloat(rupees || '0') * 100) * 10_000) / subtotalPaise)
        : 0;
  const beyondAuthority = requestedBps > maxBps;

  return (
    <Dialog
      title={t('discount.title')}
      subtitle={maxBps > 0 ? t('discount.limit', { percent: percentFromBps(maxBps) }) : t('discount.noRight')}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={beyondAuthority || kind === 'none'}
            onClick={() => {
              if (kind === 'percent') onApply({ type: 'percent', bps: requestedBps });
              else onApply({ type: 'amount', paise: Math.round(Number.parseFloat(rupees || '0') * 100) });
            }}
          >
            {t('common.apply')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          {(
            [
              ['none', t('discount.none')],
              ['percent', t('discount.percent')],
              ['amount', t('discount.amount')],
            ] as const
          ).map(([value_, label]) => (
            <button
              key={value_}
              type="button"
              className={`btn flex-1 ${kind === value_ ? 'border-till-600 bg-till-50 text-till-700' : ''}`}
              onClick={() => setKind(value_)}
            >
              {label}
            </button>
          ))}
        </div>

        {kind === 'percent' ? (
          <>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">{t('discount.percentOff')}</span>
              <input
                className="field"
                value={percent}
                inputMode="decimal"
                autoFocus
                aria-label={t('discount.percentLabel')}
                onChange={(event) => setPercent(event.target.value)}
              />
            </label>
            {beyondAuthority ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {t('discount.beyond', { requested: percentFromBps(requestedBps), limit: percentFromBps(maxBps) })}
              </p>
            ) : null}
          </>
        ) : null}

        {kind === 'amount' ? (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">{t('discount.amountLabel')}</span>
            <input
              className="field"
              value={rupees}
              inputMode="decimal"
              autoFocus
              aria-label={t('discount.amountAria')}
              onChange={(event) => setRupees(event.target.value)}
            />
          </label>
        ) : null}

        {kind !== 'none' ? (
          <p className="text-sm text-slate-500">{t('discount.subtotal', { amount: amount(subtotalPaise) })}</p>
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * Held bills.
 *
 * A held bill never touches stock, so parking one mid-queue is safe and free. Resuming
 * brings back the exact lines and prices from when it was parked, because a price change
 * in between must not silently alter a bill the customer already agreed to.
 */
export function HoldDialog({
  bills,
  canHold,
  canResume,
  onHold,
  onResume,
  onClose,
}: {
  /** `null` while the list is loading. */
  bills: HeldSale[] | null;
  /** Nothing to park when the current bill is empty. */
  canHold: boolean;
  /** Resuming replaces the screen, so it is only offered on an empty bill. */
  canResume: boolean;
  onHold: (label: string) => Promise<void>;
  onResume: (bill: HeldSale) => Promise<void>;
  onClose: () => void;
}) {
  const t = useT();
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const hold = () => {
    if (busy || !canHold) return;
    setBusy('hold');
    void onHold(label).finally(() => setBusy(null));
  };

  return (
    <Dialog title={t('hold.title')} subtitle={t('hold.subtitle')} onClose={onClose} testId="hold-dialog">
      {canHold ? (
        <div className="flex gap-2">
          <input
            className="field"
            value={label}
            placeholder={t('hold.name')}
            aria-label={t('hold.label')}
            data-testid="hold-label"
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                hold();
              }
            }}
          />
          <button type="button" className="btn btn-primary" data-testid="hold-submit" disabled={busy !== null} onClick={hold}>
            {t('hold.submit')}
          </button>
        </div>
      ) : (
        <p className="banner banner-info">{t('hold.emptyBill')}</p>
      )}
      {canHold && bills !== null && bills.length > 0 ? (
        <p className="mt-2 text-xs text-slate-500">{t('hold.holdFirst')}</p>
      ) : null}

      <div className="mt-4 border-t border-slate-200 pt-3">
        <p className="mb-2 flex items-center gap-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">
          {t('hold.list')}
          {bills ? <Badge tone="primary">{bills.length}</Badge> : null}
        </p>
        {bills === null ? (
          <p className="text-sm text-slate-500" role="status">
            {t('hold.loading')}
          </p>
        ) : bills.length === 0 ? (
          <p className="text-sm text-slate-500">{t('hold.none')}</p>
        ) : (
          <ul className="space-y-2">
            {bills.map((bill) => (
              <li key={bill.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-2.5">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-slate-800">{bill.label}</span>
                  <span className="tabular block text-xs text-slate-500">
                    {bill.holdNumber} · {t('hold.items', { count: bill.payload?.lines?.length ?? 0 })} · ₹{amount(bill.totalPaise)} ·{' '}
                    {new Date(bill.heldAt).toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn btn-touch shrink-0"
                  disabled={busy !== null || !canResume}
                  data-testid="hold-resume"
                  aria-label={t('hold.resumeLabel', { name: bill.label ?? bill.holdNumber })}
                  onClick={() => {
                    setBusy(bill.id);
                    void onResume(bill).finally(() => setBusy(null));
                  }}
                >
                  {t('hold.resume')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

/** Cash in the drawer at the start of the shift; the close-out is measured against it. */
export function OpenShiftDialog({
  busy,
  onOpen,
  onClose,
}: {
  busy: boolean;
  onOpen: (openingCashPaise: number) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [rupees, setRupees] = useState('');
  const paise = Math.round(Number.parseFloat(rupees || '0') * 100);
  const valid = Number.isFinite(paise) && paise >= 0;
  return (
    <Dialog
      title={t('shift.openTitle')}
      subtitle={t('shift.openSubtitle')}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="shift-open-submit"
            disabled={!valid || busy}
            onClick={() => onOpen(paise)}
          >
            {busy ? t('shift.opening') : t('shift.openTitle')}
          </button>
        </div>
      }
    >
      <label htmlFor="opening-cash" className="mb-1 block text-sm font-medium text-slate-700">
        {t('shift.openingCash')}
      </label>
      <input
        id="opening-cash"
        data-testid="shift-opening-cash"
        className="field tabular text-lg"
        inputMode="decimal"
        autoFocus
        placeholder="0.00"
        value={rupees}
        onChange={(event) => setRupees(event.target.value.replace(/[^\d.]/g, ''))}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && valid && !busy) onOpen(paise);
        }}
      />
    </Dialog>
  );
}

interface Reconciliation {
  openingCashPaise: number;
  cashSalesPaise: number;
  cashRefundsPaise: number;
  expectedCashPaise: number;
  differencePaise: number;
  status: 'BALANCED' | 'SHORT' | 'OVER';
}

/**
 * End-of-shift cash count. Shows what the drawer should hold, takes the counted amount and
 * asks for a reason whenever the two differ, before the shift is closed.
 */
export function CloseShiftDialog({
  shiftId,
  onClosed,
  onClose,
}: {
  shiftId: string;
  onClosed: (reconciliation: Reconciliation) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [rupees, setRupees] = useState('');
  const [note, setNote] = useState('');
  const [expected, setExpected] = useState<Reconciliation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ reconciliation: Reconciliation }>(`/api/pos/shifts/${shiftId}/reconciliation`, { actualPaise: 0 })
      .then((value) => setExpected(value.reconciliation))
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : tr('shift.loadFailed')));
  }, [shiftId]);

  const counted = Math.round(Number.parseFloat(rupees || 'NaN') * 100);
  const hasCount = Number.isFinite(counted) && counted >= 0;
  const difference = expected && hasCount ? counted - expected.expectedCashPaise : 0;
  const needsNote = hasCount && difference !== 0;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ reconciliation: Reconciliation }>(`/api/pos/shifts/${shiftId}/close`, {
        actualCashPaise: counted,
        differenceNote: note.trim() || null,
      });
      onClosed(result.reconciliation);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : tr('shift.closeFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t('shift.closeTitle')}
      subtitle={t('shift.closeSubtitle')}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="shift-close-submit"
            disabled={!hasCount || (needsNote && !note.trim()) || busy || !expected}
            onClick={() => void submit()}
          >
            {busy ? t('shift.closing') : t('shift.closeTitle')}
          </button>
        </div>
      }
    >
      {expected ? (
        <dl className="mb-4 grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-500">{t('shift.opened')}</dt>
          <dd className="tabular text-right">₹{amount(expected.openingCashPaise)}</dd>
          <dt className="text-slate-500">{t('shift.cashSales')}</dt>
          <dd className="tabular text-right">₹{amount(expected.cashSalesPaise)}</dd>
          <dt className="text-slate-500">{t('shift.cashRefunds')}</dt>
          <dd className="tabular text-right">−₹{amount(expected.cashRefundsPaise)}</dd>
          <dt className="font-semibold text-slate-900">{t('shift.expected')}</dt>
          <dd className="tabular text-right font-semibold">₹{amount(expected.expectedCashPaise)}</dd>
        </dl>
      ) : (
        <p className="mb-4 text-sm text-slate-500">{t('shift.loading')}</p>
      )}
      <label htmlFor="counted-cash" className="mb-1 block text-sm font-medium text-slate-700">
        {t('shift.counted')}
      </label>
      <input
        id="counted-cash"
        data-testid="shift-counted-cash"
        className="field tabular text-lg"
        inputMode="decimal"
        autoFocus
        placeholder="0.00"
        value={rupees}
        onChange={(event) => setRupees(event.target.value.replace(/[^\d.]/g, ''))}
      />
      {hasCount && expected ? (
        <p
          className={`mt-2 text-sm font-semibold ${difference === 0 ? 'text-emerald-700' : 'text-red-700'}`}
          role="status"
        >
          {difference === 0
            ? t('shift.balances')
            : difference < 0
              ? t('shift.short', { amount: amount(-difference) })
              : t('shift.over', { amount: amount(difference) })}
        </p>
      ) : null}
      {needsNote ? (
        <>
          <label htmlFor="difference-note" className="mb-1 mt-3 block text-sm font-medium text-slate-700">
            {t('shift.reason')}
          </label>
          <textarea
            id="difference-note"
            className="field"
            rows={2}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </>
      ) : null}
      {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
    </Dialog>
  );
}

/**
 * "No Tax" for the whole bill. Shows what the bill comes down to before it is applied, takes an
 * optional reason (kept on the invoice and in the audit trail), and says how it will be recorded.
 */
export function NoTaxDialog({
  currentPaise,
  reducedPaise,
  mode,
  onApply,
  onClose,
}: {
  currentPaise: number;
  reducedPaise: number;
  mode: 'discount' | 'exempt';
  onApply: (note: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [note, setNote] = useState('');
  return (
    <Dialog
      title={t('noTax.title')}
      subtitle={t('noTax.becomes', { from: amount(currentPaise), to: amount(reducedPaise) })}
      onClose={onClose}
      testId="no-tax-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" data-testid="no-tax-apply" onClick={() => onApply(note)}>
            {t('noTax.apply')}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-700">
          {t('noTax.pays', { amount: amount(reducedPaise), less: amount(Math.max(0, currentPaise - reducedPaise)) })}{' '}
          {mode === 'discount' ? t('noTax.asDiscount') : t('noTax.asExempt')}
        </p>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">{t('noTax.reason')}</span>
          <input
            className="field"
            value={note}
            maxLength={200}
            autoFocus
            aria-label={t('noTax.reasonAria')}
            data-testid="no-tax-note"
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
        <p className="text-xs text-slate-500">{t('noTax.audit')}</p>
      </div>
    </Dialog>
  );
}

/**
 * Open item: a name and a price typed at the till for something that is not in the catalogue.
 * Whole units only, a price above zero, a name of 1 to 60 characters (Tamil welcome). It never
 * touches stock. Above the store's limit the server asks for a manager; the dialog says so first.
 */
export function OpenItemDialog({
  maxPaise,
  canExceedMax,
  onAdd,
  onClose,
}: {
  maxPaise: number;
  canExceedMax: boolean;
  onAdd: (input: { name: string; ratePaise: number; qtyMilli: number }) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [qty, setQty] = useState('1');
  const nameProblem = openItemNameProblem(name);
  const ratePaise = Math.round(Number.parseFloat(price || '0') * 100);
  const units = Number.parseInt(qty || '0', 10);
  const lineTotal = Number.isFinite(ratePaise) && units > 0 ? ratePaise * units : 0;
  const priceProblem = !(ratePaise > 0) ? t('openItem.priceProblem') : null;
  const qtyProblem = !(units > 0) || String(units) !== qty.trim() ? t('openItem.qtyProblem') : null;
  const overLimit = maxPaise > 0 && lineTotal > maxPaise && !canExceedMax;
  const ready = !nameProblem && !priceProblem && !qtyProblem && !overLimit;

  return (
    <Dialog
      title={t('openItem.title')}
      subtitle={t('openItem.subtitle')}
      onClose={onClose}
      testId="open-item-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="open-item-add"
            disabled={!ready}
            onClick={() => onAdd({ name: normalizeOpenItemName(name), ratePaise, qtyMilli: units * 1000 })}
          >
            {t('openItem.add')}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">{t('openItem.name')}</span>
          <input className="field" value={name} maxLength={80} autoFocus aria-label={t('openItem.nameAria')} data-testid="open-item-name" onChange={(event) => setName(event.target.value)} />
          {name && nameProblem ? <span className="mt-1 block text-xs text-red-700">{nameProblem}</span> : null}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">{t('openItem.price')}</span>
            <input className="field tabular" value={price} inputMode="decimal" aria-label={t('openItem.priceAria')} data-testid="open-item-price" onChange={(event) => setPrice(event.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">{t('openItem.qty')}</span>
            <input className="field tabular" value={qty} inputMode="numeric" aria-label={t('openItem.qtyAria')} data-testid="open-item-qty" onChange={(event) => setQty(event.target.value)} />
          </label>
        </div>
        {price && priceProblem ? <p className="text-xs text-red-700">{priceProblem}</p> : null}
        {qtyProblem ? <p className="text-xs text-red-700">{qtyProblem}</p> : null}
        {overLimit ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert">
            {t('openItem.overLimit', { amount: amount(lineTotal), limit: amount(maxPaise) })}
          </p>
        ) : null}
        <p className="text-xs text-slate-500">{t('openItem.noTax')}</p>
      </div>
    </Dialog>
  );
}

/**
 * Item Tax: pick a GST rate for each open item on the bill. Catalogue items carry their own rate
 * and are not listed. Whether the typed price includes the tax is the store's setting.
 */
export function ItemTaxDialog({
  lines,
  rates,
  priceIncludesTax,
  onChoose,
  onClose,
}: {
  lines: { key: string; name: string; taxRateId: string | null }[];
  rates: { id: string; name: string; gstBps: number; cessBps: number }[];
  priceIncludesTax: boolean;
  onChoose: (key: string, rateId: string | null) => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <Dialog
      title={t('itemTax.title')}
      subtitle={priceIncludesTax ? t('itemTax.inclusive') : t('itemTax.onTop')}
      onClose={onClose}
      testId="item-tax-dialog"
      footer={
        <div className="flex justify-end">
          <button type="button" className="btn btn-primary" onClick={onClose} data-testid="item-tax-done">
            {t('common.done')}
          </button>
        </div>
      }
    >
      <ul className="space-y-2">
        {lines.map((line) => (
          <li key={line.key} className="flex items-center justify-between gap-3">
            <span className="min-w-0 truncate text-sm font-medium text-slate-800">{line.name}</span>
            <select
              className="field w-44"
              aria-label={t('itemTax.for', { name: line.name })}
              data-testid="item-tax-rate"
              value={line.taxRateId ?? ''}
              onChange={(event) => onChoose(line.key, event.target.value || null)}
            >
              <option value="">{t('itemTax.none')}</option>
              {rates
                .filter((rate) => rate.gstBps > 0 || rate.cessBps > 0)
                .map((rate) => (
                  <option key={rate.id} value={rate.id}>
                    {rate.name}
                  </option>
                ))}
            </select>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
